import { useEffect, useMemo, useState, type JSX } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { PlatformIcon } from '@/components/PlatformIcon';
import { ApiError, type RepeatSchedule, type SearchParamValue } from '../api/types';
import { FieldApiError, type ScheduleParamsPreview } from '../api/client';
import { FieldError, Segmented } from '../search/controls';
import { FilterGroups, GroupFields } from '../search/FilterGroups';
import { QueryInput } from '../search/QueryInput';
import { useSearchApi, useSearchSchema } from '../search/hooks';
import {
  activeFilters,
  fromScrapeParams,
  groupsWithErrors,
  mapFieldErrors,
  modesFor,
  toScrapeParams,
  validateForm,
  type MappedFieldErrors,
  type SearchFormState,
} from '../search/params';
import { Note } from '../workspace/ui';
import { ChangesList } from './ScheduleSettings';

const NO_ERRORS: MappedFieldErrors = { filters: {}, query: null, other: [] };

/**
 * Change what a scheduled search runs with, in place — the same form fields
 * as "Find creators", prefilled from the schedule's stored search. Saving is
 * two steps on purpose: the server says exactly what would change (countries
 * as added / removed names) before anything is stored, because a schedule
 * spends these settings unattended on every run.
 *
 * The platform stays fixed: a schedule's history and folder are tied to it.
 */
export function ScheduleEditDialog({
  schedule,
  onClose,
}: {
  schedule: RepeatSchedule;
  onClose: () => void;
}): JSX.Element {
  const api = useSearchApi();
  const qc = useQueryClient();
  const { schema, isLoading: schemaLoading } = useSearchSchema();
  const folderId = schedule.folder?.publicId ?? '';

  const [form, setForm] = useState<SearchFormState | null>(null);
  const [attempted, setAttempted] = useState(false);
  const [serverErrors, setServerErrors] = useState<MappedFieldErrors>(NO_ERRORS);
  const [problem, setProblem] = useState<string | null>(null);
  const [preview, setPreview] = useState<ScheduleParamsPreview | null>(null);
  const [busy, setBusy] = useState<'preview' | 'save' | null>(null);

  // Prefill once the real schema is in, so fields aren't built from the fallback and then rebuilt.
  useEffect(() => {
    if (form || schemaLoading || !schedule.params) return;
    setForm(fromScrapeParams(schema, schedule.params));
  }, [form, schemaLoading, schema, schedule.params]);

  const clientErrors = useMemo(() => (form ? validateForm(schema, form) : {}), [schema, form]);
  const errors = useMemo(() => {
    const { query: _q, ...rest } = clientErrors;
    return { ...rest, ...serverErrors.filters };
  }, [clientErrors, serverErrors]);
  const queryError = serverErrors.query ?? (attempted ? clientErrors.query ?? null : null);
  const active = useMemo(() => (form ? activeFilters(schema, form) : []), [schema, form]);
  const forceOpen = useMemo(() => groupsWithErrors(schema, errors), [schema, errors]);
  const modes = form ? modesFor(schema, form.platform) : [];
  const mode = form ? modes.find((m) => m.id === form.mode) ?? modes[0] : undefined;

  function change(next: SearchFormState): void {
    setForm(next);
    if (serverErrors.query || serverErrors.other.length) setServerErrors((e) => ({ ...e, query: null, other: [] }));
  }
  function changeParam(key: string, v: SearchParamValue): void {
    if (!form) return;
    setForm({ ...form, params: { ...form.params, [key]: v } });
    if (serverErrors.filters[key]) {
      setServerErrors((e) => {
        const { [key]: _gone, ...filters } = e.filters;
        return { ...e, filters };
      });
    }
  }

  function fail(err: unknown, doing: string): void {
    if (err instanceof FieldApiError && err.fieldErrors) {
      setServerErrors(mapFieldErrors(err.fieldErrors, schema));
      setProblem(err.message || 'Some settings need fixing.');
      setPreview(null);
    } else if (err instanceof ApiError && err.status === 404) {
      setProblem("Settings can't be edited yet: the Discovery service needs its latest update. Try again in a few minutes.");
    } else {
      setProblem(`Couldn't ${doing}: ${(err as Error).message}`);
    }
  }

  async function review(): Promise<void> {
    if (!form || busy) return;
    setAttempted(true);
    setProblem(null);
    if (Object.keys(clientErrors).length > 0) return;
    setBusy('preview');
    try {
      setPreview(await api.scheduleSettings.preview(schedule.publicId, toScrapeParams(schema, form, folderId)));
      setServerErrors(NO_ERRORS);
    } catch (err) {
      fail(err, 'check these settings');
    } finally {
      setBusy(null);
    }
  }

  async function save(): Promise<void> {
    if (!form || busy) return;
    setBusy('save');
    setProblem(null);
    try {
      await api.scheduleSettings.save(schedule.publicId, toScrapeParams(schema, form, folderId));
      void qc.invalidateQueries({ queryKey: ['discovery', 'repeat-runs'] });
      if (folderId) void qc.invalidateQueries({ queryKey: ['discovery', 'schedules', folderId] });
      toast.success(`Updated "${schedule.name}"`, { description: 'Its next run uses the new settings.' });
      onClose();
    } catch (err) {
      fail(err, 'save the settings');
    } finally {
      setBusy(null);
    }
  }

  return (
    <Dialog open onOpenChange={(open) => !open && !busy && onClose()}>
      <DialogContent className="flex max-h-[90vh] flex-col sm:max-w-3xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <PlatformIcon platform={schedule.platform} size={16} className="shrink-0 text-muted-foreground" />
            <span className="min-w-0 truncate">{preview ? 'Review changes' : `Edit settings: ${schedule.name}`}</span>
          </DialogTitle>
          <DialogDescription>
            {preview
              ? 'Check what changes. Every run from now on uses the new settings; past runs keep the settings they ran with.'
              : `What this search runs with every time it repeats${schedule.folder ? ` into ${schedule.folder.name}` : ''}.`}
          </DialogDescription>
        </DialogHeader>

        <div className="min-h-0 flex-1 space-y-5 overflow-y-auto pr-1">
          {!schedule.params ? (
            <Note tone="info">
              This schedule's settings aren't available yet. The Discovery service needs its latest update before they
              can be edited.
            </Note>
          ) : !form ? (
            <p className="text-sm text-muted-foreground">Loading the form…</p>
          ) : preview ? (
            preview.changed ? (
              <ChangesList changes={preview.changes} />
            ) : (
              <Note tone="info">Nothing changes: these are the settings the schedule already runs with.</Note>
            )
          ) : (
            <>
              <section aria-label="Query" className="space-y-4">
                {modes.length > 1 && (
                  <Segmented
                    label="Search mode"
                    value={form.mode}
                    onChange={(m) => change({ ...form, mode: m })}
                    options={modes.map((m) => ({ value: m.id, label: m.label }))}
                  />
                )}
                {mode && (
                  <QueryInput
                    platform={form.platform}
                    mode={mode}
                    query={form.query}
                    sounds={form.sounds}
                    onQueryChange={(query) => change({ ...form, query })}
                    onSoundsChange={(sounds) => change({ ...form, sounds })}
                    error={queryError}
                    folderSeeds={[]}
                  />
                )}
                <GroupFields schema={schema} group="search" state={form} onChange={changeParam} errors={errors} />
              </section>
              <FilterGroups
                schema={schema}
                state={form}
                onChange={changeParam}
                errors={errors}
                active={active}
                forceOpen={forceOpen}
                skip={['search']}
              />
              {serverErrors.other.map((e) => (
                <FieldError key={e} message={e} />
              ))}
            </>
          )}
          {problem && (
            <Note tone="error" role="alert">
              {problem}
            </Note>
          )}
        </div>

        <DialogFooter className="gap-2">
          {preview ? (
            <>
              <Button type="button" variant="ghost" onClick={() => setPreview(null)} disabled={!!busy}>
                <ArrowLeft className="h-3.5 w-3.5" />
                Back to editing
              </Button>
              <Button type="button" onClick={() => void save()} disabled={!!busy || !preview.changed}>
                {busy === 'save' && <Loader2 className="h-4 w-4 animate-spin" />}
                {busy === 'save' ? 'Saving…' : 'Save settings'}
              </Button>
            </>
          ) : (
            <>
              <Button type="button" variant="ghost" onClick={onClose} disabled={!!busy}>
                Cancel
              </Button>
              <Button type="button" onClick={() => void review()} disabled={!!busy || !form}>
                {busy === 'preview' && <Loader2 className="h-4 w-4 animate-spin" />}
                {busy === 'preview' ? 'Checking…' : 'Review changes'}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
